import { unsubscribe } from "pubsub-js";
import {
	Button,
	getPropertySortText,
	SearchInputForm,
	OptionsMenu,
	OptionsMenuItem,
	ContextActionBar,
	PageContentTitle,
	ToolbarAction,
	SearchPlaceholder,
	DropZone
} from "client/Components";
import {
	Dispatch,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { createUrlRelativeToEnvironment, navigateTo } from "client_legacy/ndaw/navigator";
import { WebDavEvent, subscribe } from "client_legacy/Documents/WebDav/events";
import { BookmarkEvent, subscribe as bookmarkSubscribe } from "client_legacy/Documents/Bookmarks/events";
import { WebDavBreadCrumb } from "./WebDavBreadCrumb";
import { GridItem, WebDavOverview } from "./WebDavOverview";
import { DocumentStoreType } from "client/Api/Documents/common";
import { DocumentPageLayout } from "../DocumentPageLayout";
import { newGuid } from "client_legacy/ndaw/guid";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { FolderNavigationStackItem } from "./useFolderNavigationStack";
import {
	getContent,
	FileSystemItemModel,
	WebDavServerModel
} from "client/Api/Documents/WebDav/webDav";
import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import { ActionsWithContext } from "client_legacy/ndaw/actions";
import { WebDavDocumentActionContext } from "./actions";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { FileSelectionEntry } from "client_legacy/FileUploader/utils";
import { DocumentSelect, DocumentSelectHandle } from "../DocumentSelect/DocumentSelect";
import { UploadBatchHandlers } from "../DocumentUploader/useDocumentUploader";
import { useWebDavDocumentUploaderHandlers } from "./useWebDavDocumentUploaderHandlers";

type LoadResult = TryResult<readonly FileSystemItemModel[]>;

export type WebDavPageContentProps = {
	isMobile: boolean;
	isResponsive: boolean;
	server: WebDavServerModel;
	folderPath: readonly FolderNavigationStackItem[];
	webDavConfiguration: WebDavConfiguration;
	onOpenFolder: (item: FolderNavigationStackItem) => void;
	layoutKind: DocumentLayoutKind;
	onLayoutKindChange(kind: DocumentLayoutKind): void;
	onDocumentUpload: (
		files: readonly FileSelectionEntry[],
		handlers: UploadBatchHandlers
	) => void;
};

export const WebDavPageContent = (props: WebDavPageContentProps): ReactElement => {
	const {
		isMobile,
		isResponsive,
		server,
		folderPath,
		webDavConfiguration,
		onOpenFolder,
		layoutKind,
		onLayoutKindChange,
		onDocumentUpload
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({ value: "", version: newGuid() });
	const [isLoadingModels, setIsLoadingModels] = useState<boolean>(false);
	const [loadResult, setLoadResult] = useState<LoadResult>({
		wasSuccessful: true,
		result: []
	});
	const [sorting, setSorting] = useState<DataSourceSortableState<GridItem>>({
		propertyName: "nameSorting",
		direction: DataSourceSortDirection.Ascending
	});
	const [actionsWithContext, setActionsWithContext] = useState<WebDavActionsWithContext | null>(null);

	const documentSelectRef: RefObject<DocumentSelectHandle | null> = useRef<DocumentSelectHandle>(null);

	const currentFolder = useMemo(
		(): FolderNavigationStackItem | undefined => {
			return folderPath[folderPath.length - 1];
		},
		[folderPath]
	);

	const setModels = useCallback(
		(stateMutator: (prevState: readonly FileSystemItemModel[]) => readonly FileSystemItemModel[]): void => {
			setLoadResult(
				(x): LoadResult => {
					const prevState: readonly FileSystemItemModel[] = x.wasSuccessful
						? x.result
						: [];

					const newState: readonly FileSystemItemModel[] = stateMutator(prevState);
					if (x.wasSuccessful) {
						return {
							...x,
							result: newState
						};
					} else {
						return x;
					}
				}
			);
		},
		[]
	);

	usePubSubEvents(
		server.id,
		currentFolder?.id,
		setModels
	);

	useEffect(
		(): (() => void) | undefined => {
			if (!currentFolder) {
				return;
			}

			async function loadModels(
				serverId: number,
				folderId: string,
				signal: AbortSignal
			): Promise<void> {
				setLoadResult({
					result: [],
					wasSuccessful: true
				});

				setIsLoadingModels(true);

				try {
					const models: readonly FileSystemItemModel[] = await getContent(serverId, folderId, signal);
					setIsLoadingModels(false);
					setLoadResult({
						wasSuccessful: true,
						result: models
					});
				} catch (e: unknown) {
					if (isCancelledError(e)) {
						return;
					}

					setIsLoadingModels(false);
					setLoadResult({
						wasSuccessful: false
					});
				}
			}

			const cancellation = new AbortController();
			loadModels(server.id, currentFolder.id, cancellation.signal);
			return () => cancellation.abort();
		},
		[server, currentFolder]
	);

	const updateCredentialsHandler = (): void => {
		navigateTo(
			createUrlRelativeToEnvironment("/IntegrationCredentials/EditFileServerCredentials")
		);
	};

	useEffect(
		() => {
			setSearchTerm({
				value: "",
				version: newGuid()
			});
		},
		[currentFolder]
	);

	const actionsChangeHandler = useCallback(
		(value: WebDavActionsWithContext): (() => void) => {
			setActionsWithContext(value);
			return () => setActionsWithContext(null);
		},
		[]
	);

	const documentUploaderHandlers: UploadBatchHandlers | undefined = useWebDavDocumentUploaderHandlers(
		server,
		currentFolder,
		webDavConfiguration,
		onOpenFolder
	);

	const documentSelectHandler = useMemo(
		(): ((files: readonly FileSelectionEntry[]) => void) | undefined => {
			if (!documentUploaderHandlers) {
				return undefined;
			}

			return (files: readonly FileSelectionEntry[]): void => {
				onDocumentUpload(
					files,
					documentUploaderHandlers
				);
			};

		},
		[
			documentUploaderHandlers,
			onDocumentUpload
		]
	);

	const uploadFolderHandler = useCallback(
		(): void => {
			documentSelectRef.current
				?.selectFolder();
		},
		[]
	);

	const uploadFilesHandler = useCallback(
		(): void => {
			documentSelectRef.current
				?.selectFiles();
		},
		[]
	);

	const shouldShowActionBar = useMemo(
		(): boolean => {
			if (isMobile) {
				return true;
			}

			const selectionCount: number = actionsWithContext?.context
				.selectedDocuments
				.length ?? 0;

			return selectionCount > 0;
		},
		[
			actionsWithContext?.context
				.selectedDocuments
				.length,
			isMobile
		]
	);

	const areOverviewControlsDisabled: boolean = !loadResult.wasSuccessful;

	return (
		<>
			{folderPath.length > 1 && (
				<TitleBarBackButton
					onBack={() => onOpenFolder(folderPath[folderPath.length - 2])}
				/>
			)}

			<DropZone
				trackingId="drop-zone"
				height="fill"
			>
				<DocumentPageLayout trackingId="webdav-page-content">
					<DocumentPageLayout.Header>
						<DocumentPageLayout.BreadCrumb>
							<WebDavBreadCrumb
								items={folderPath}
								onOpenFolder={onOpenFolder}
							/>
						</DocumentPageLayout.BreadCrumb>
						{shouldShowActionBar
							? (
								<DocumentPageLayout.ActionBarContainer>
									<ContextActionBar
										{...(
												actionsWithContext
													? actionsWithContext
													: { actions: EMPTY_ARRAY, context: undefined! /* There are no actions, so no need for a context */ }
										)}
										trackingId="global-action-bar"
										horizontalAlignment={isMobile ? "default" : "page-content"}
										hasBorderBottom={isMobile}
									/>
								</DocumentPageLayout.ActionBarContainer>
							)
							: (
								<PageContentTitle
									{...(
										actionsWithContext
											? { actionContext: actionsWithContext.context, actions: actionsWithContext.actions}
											: { actions: undefined, actionContext: undefined }
									)}
									title={folderPath.length > 0
										? folderPath[folderPath.length - 1].name
										: ""
									}
								/>
							)
						}

						<DocumentPageLayout.SortAndFilter>
							<SearchInputForm
								value={searchTerm}
								onChange={(value, version) => setSearchTerm({ value, version })}
								trackingId="search-term"
								placeholder={
									<SearchPlaceholder
										kind="search"
										properties={[localize("_Name")]}
									/>
								}
								height="large"
								isDisabled={areOverviewControlsDisabled}
							/>

							{(isMobile || layoutKind === DocumentLayoutKind.Grid) && (
								<OptionsMenu
									value={sorting}
									icon="arrow-up-arrow-down"
									onChange={setSorting}
									options={webDavSortMenuOptions}
									trackingId="sort-menu"
									title={localize("_SortBy")}
									buttonSize="large"
									isDisabled={areOverviewControlsDisabled}
								/>
							)}

							<DocumentPageLayout.LayoutKindChangeButton
								layoutKind={layoutKind}
								onLayoutKindChange={onLayoutKindChange}
								isDisabled={areOverviewControlsDisabled}
							/>
						</DocumentPageLayout.SortAndFilter>
					</DocumentPageLayout.Header>

					<DocumentPageLayout.Content>
						{currentFolder && (
							<>
								{loadResult.wasSuccessful
									? (
										<WebDavOverview
											isMobile={isMobile}
											server={server}
											folder={currentFolder}
											models={loadResult.result}
											isLoadingModels={isLoadingModels}
											onOpenFolder={onOpenFolder}
											searchTerm={searchTerm.value}
											sorting={sorting}
											setSorting={setSorting}
											webDavConfiguration={webDavConfiguration}
											layoutKind={layoutKind}
											onActionsChanged={actionsChangeHandler}
											isResponsive={isResponsive}
											onUploadFolder={uploadFolderHandler}
											onUploadFiles={uploadFilesHandler}
										/>
									)
									: (
										<p>
											{localize("_WebDavThisFolderCanNotBeOpened")}
											<br />
											<br />
											<Button
												kind="primary"
												text={localize("_UpdateCredentials")}
												onClick={updateCredentialsHandler}
											/>
										</p>
									)
								}
							</>
					)}
					</DocumentPageLayout.Content>

					{isNotNil(documentSelectHandler) && (
						<DocumentSelect
							onDocumentSelect={documentSelectHandler}
							ref={documentSelectRef}
						/>
					)}
				</DocumentPageLayout>
			</DropZone>
		</>
	);
};
WebDavPageContent.displayName = "WebDavPageContent";

function usePubSubEvents(
	currentServerId: number,
	currentFolderId: string | undefined,
	setModels: Dispatch<(prevState: readonly FileSystemItemModel[]) => readonly FileSystemItemModel[]>
): void {
	useEffect(
		() => {
			const subscriptionTokens: readonly PubSubSubscriptionToken[] = registerSubscriptions(
				currentServerId,
				currentFolderId,
				setModels
			);

			return () => {
				for (const token of subscriptionTokens) {
					unsubscribe(token);
				}
			};
		},
		[
			currentFolderId,
			currentServerId,
			setModels
		]
	);
}

function registerSubscriptions(
	currentServerId: number,
	currentFolderId: string | undefined,
	setModels: Dispatch<(prevState: readonly FileSystemItemModel[]) => readonly FileSystemItemModel[]>
): readonly PubSubSubscriptionToken[] {
	const tokens: PubSubSubscriptionToken[] = [];

	const tryRemoveItemsById = (
		items: readonly FileSystemItemModel[],
		ids: readonly string[]
	): readonly FileSystemItemModel[] => {
		const result: readonly FileSystemItemModel[] = items.filter(x => !ids.includes(x.id));
		if (result.length === items.length) {
			return items;
		}
		return result;
	};

	const tryAddItem = (
		items: readonly FileSystemItemModel[],
		item: FileSystemItemModel
	): readonly FileSystemItemModel[] => {
		if (item.parentId !== currentFolderId) {
			return items;
		}

		return [...items, item];
	};

	const itemDeletedToken: PubSubSubscriptionToken = subscribe(
		WebDavEvent.Deleted,
		(_, data) => {
			if (data.serverId !== currentServerId) {
				return;
			}

			setModels((x) => tryRemoveItemsById(x, data.documentIds));
		}
	);
	tokens.push(itemDeletedToken);

	const itemCreateToken: PubSubSubscriptionToken = subscribe(
		WebDavEvent.CreatedOrReplaced,
		(_, data) => {
			if (data.serverId !== currentServerId) {
				return;
			}

			setModels(
				(items) => {
					const index: number = items.findIndex(x => x.id === data.item.id);
					if (index === -1) {
						return tryAddItem(items, data.item);
					} else {
						const copy: FileSystemItemModel[] = [...items];
						copy.splice(
							index,
							1,
							data.item
						);
						return copy;
					}
				}
			);
		}
	);
	tokens.push(itemCreateToken);

	const itemRenameToken: PubSubSubscriptionToken = subscribe(
		WebDavEvent.Renamed,
		(_, data) => {
			if (data.serverId !== currentServerId) {
				return;
			}

			setModels(
				(items) => {
					return items.map(
						x => {
							if (x.id === data.oldItemId) {
								return {
									...data.item,
									bookmarkId: x.bookmarkId
								};
							}

							return x;
						}
					);
				}
			);
		}
	);
	tokens.push(itemRenameToken);

	const itemMoveToken: PubSubSubscriptionToken = subscribe(
		WebDavEvent.Moved,
		(_, data) => {
			if (data.serverId !== currentServerId) {
				return;
			}

			return setModels(
				(items) => {

					let result: readonly FileSystemItemModel[] = items;
					result = tryRemoveItemsById(result, [data.oldItemId]);
					result = tryAddItem(result, data.item);
					return result;
				}
			);
		}
	);
	tokens.push(itemMoveToken);

	const bookmarkAddedToken: PubSubSubscriptionToken = bookmarkSubscribe(
		BookmarkEvent.Added,
		(_, data) => {
			if (data.storeType !== DocumentStoreType.WebDav) {
				return;
			}

			const { serverId, fileId } = data;
			if (serverId !== currentServerId) {
				return;
			}

			setModels(
				(items) => {
					const index: number = items.findIndex(x => x.id === fileId);
					if (index === -1) {
						return items;
					} else {
						const copy: FileSystemItemModel[] = [...items];
						const mutatedItem: FileSystemItemModel = {
							...copy[index],
							bookmarkId: data.id
						};
						copy.splice(
							index,
							1,
							mutatedItem
						);
						return copy;
					}
				}
			);
		}
	);
	tokens.push(bookmarkAddedToken);

	const bookmarkDeletedToken: PubSubSubscriptionToken =
	bookmarkSubscribe(
		BookmarkEvent.Deleted,
		(_, data) => {
			setModels(
				(items) => items.map(
					x => {
						const hadBookmarkDeleted: boolean = x.bookmarkId !== null && data.includes(x.bookmarkId);
						if (hadBookmarkDeleted) {
							return {
								...x,
								bookmarkId: null
							};
						}

						return x;
					}
				)
			);
		}
	);
	tokens.push(bookmarkDeletedToken);

	return tokens;
};

export const webDavSortMenuOptions: readonly OptionsMenuItem<DataSourceSortableState<GridItem>>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "nameSorting", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "nameSorting", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	},
	{
		text: getPropertySortText("_DateModified", "_NewToOld"),
		value: { propertyName: "modifiedDateSorting", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "modified-on-new-old"
	},
	{
		text: getPropertySortText("_DateModified", "_OldToNew"),
		value: { propertyName: "modifiedDateSorting", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "modified-on-old-new"
	},
	{
		text: getPropertySortText("_Extension", "_AToZ"),
		value: { propertyName: "extension", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "extension-a-z"
	},
	{
		text: getPropertySortText("_Extension", "_ZToA"),
		value: { propertyName: "extension", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "extension-a-z"
	},
	{
		text: getPropertySortText("_FileSize", "_SmallToLarge"),
		value: { propertyName: "size", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "file-size-small-large"
	},
	{
		text: getPropertySortText("_FileSize", "_LargeToSmall"),
		value: { propertyName: "size", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "file-size-large-small"
	}
];

export type WebDavActionsWithContext = ActionsWithContext<
	WebDavDocumentActionContext,
	ToolbarAction<WebDavDocumentActionContext>
>;